import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetAllowedTokensParameterGroupQuery,
  useGetDomicileClassParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability'
import ProductCatalogs from 'Services/ProductManagement/Features/Product/Components/ProductCatalogs'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import { ProductPricing } from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { DOMICILE_CLASS_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricing'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/Features/Tariff/Pages/TariffProductListPage'
import { TariffProductTabType } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { isTariffEditAllowed } from 'Services/ProductManagement/Features/Tariff/utils'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import useTypedSelector from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: (productId: string) => void
  productGroupParameterGroup: ParameterGroup | null
  isProductGroupParameterGroupLoading: boolean
  customerGroupParameterGroup: ParameterGroup | null
  isCustomerGroupParameterGroupLoading: boolean
}

export function TariffProductDrawer({
  open,
  handleClose,
  handleProductDelete,
  productGroupParameterGroup,
  isProductGroupParameterGroupLoading,
  customerGroupParameterGroup,
  isCustomerGroupParameterGroupLoading
}: Readonly<TariffProductDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useTypedSelector(state => state.tariff)
  const [isEdited, setIsEdited] = useState(false)
  const [isAvailabilityEdited, setIsAvailabilityEdited] = useState(false)

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })
  const {
    data: allowedTokensParameterGroup = null,
    isLoading: isAllowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(undefined, {
    skip: !open
  })
  const {
    data: domicileClassParameterGroup = null,
    isLoading: isDomicileClassParameterGroupLoading
  } = useGetDomicileClassParameterGroupQuery(undefined, {
    skip: !open
  })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffProductTabType.DETAILS
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const productDeleteModalMessage = () => {
    if (singleProduct && singleProduct.catalog_ids.length > 0) {
      return t`This product has not been removed from all catalogs, are you sure you want to delete the product?`
    }
    return t`Are you sure you want to delete the product?`
  }

  const hasPricingPlanOrAvailabilityUnsavedChanges =
    (isEdited &&
      searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) === TariffProductTabType.PRICING) ||
    (isAvailabilityEdited &&
      searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) === TariffProductTabType.AVAILABILITY)

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffProductTabType
  ) => {
    if (hasPricingPlanOrAvailabilityUnsavedChanges) {
      confirm({
        title: t`Are you sure?`,
        message: t`All changes will be lost. Are you sure you want to cancel?`,
        handleConfirm: () => {
          searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
          if (newValue !== TariffProductTabType.PRICING) {
            searchParams.delete(DOMICILE_CLASS_PARAMETER_KEY)
          }
          setSearchParams(searchParams)
        }
      })
    } else {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      if (newValue !== TariffProductTabType.PRICING) {
        searchParams.delete(DOMICILE_CLASS_PARAMETER_KEY)
      }
      setSearchParams(searchParams)
    }
    setIsAvailabilityEdited(false)
  }

  const handleDrawerClose = () => {
    if (hasPricingPlanOrAvailabilityUnsavedChanges) {
      confirm({
        title: t`Are you sure?`,
        message: t`All changes will be lost. Are you sure you want to cancel?`,
        handleConfirm: () => {
          handleClose()
        }
      })
    } else {
      handleClose()
    }
    setIsAvailabilityEdited(false)
  }

  const isSingleProductLoading = singleProductLoading === 'pending'

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {singleProduct && isTariffEditAllowed(singleTariff) && (
            <>
              <DeleteButton
                sx={{ mr: 1 }}
                size="small"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: productDeleteModalMessage(),
                    handleConfirm: () => {
                      handleProductDelete(singleProduct.id)
                    }
                  })
                }
              >{t`Delete`}</DeleteButton>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/${tariffId}/products/${singleProduct.id}/edit`)}
                size="small"
                startIcon={<EditIcon />}
              >{t`Edit`}</CustomButton>
            </>
          )}
        </Box>
      }
    >
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Tariff product tab list`}
        >
          <Tab label={t`Product details`} value={TariffProductTabType.DETAILS} />
          <Tab label={t`Pricing`} value={TariffProductTabType.PRICING} />
          <Tab label={t`Product's sales channels`} value={TariffProductTabType.SALES_CHANNELS} />
          <Tab label={t`Availability`} value={TariffProductTabType.AVAILABILITY} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.DETAILS}>
          <WithLoader loading={isVatDefinitionsLoading || isSingleProductLoading}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                productGroupParameterGroup={productGroupParameterGroup}
                isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
                customerGroupParameterGroup={customerGroupParameterGroup}
                isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
                allowedTokensParameterGroup={allowedTokensParameterGroup}
                isAllowedTokensParameterGroupLoading={isAllowedTokensParameterGroupLoading}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.PRICING}>
          <WithLoader
            rows={ROWS_PER_PAGE_DEFAULT}
            loading={isSingleProductLoading || isDomicileClassParameterGroupLoading}
          >
            {singleProduct && (
              <ProductPricing
                product={singleProduct}
                domicileClassParameterGroup={domicileClassParameterGroup}
                isEdited={isEdited}
                setIsEdited={setIsEdited}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.SALES_CHANNELS}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && <ProductCatalogs product={singleProduct} handleClose={handleClose} />}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.AVAILABILITY}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
                isEdited={isAvailabilityEdited}
                setIsEdited={setIsAvailabilityEdited}
              />
            )}
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}
